import { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  Box,
  DialogContent,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Typography,
  DialogActions,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { AtticusForm } from "../../../models/AtticusForm";
import { PdfFileChoice } from "./useAtticusFormViewer";
import { SplitButtonOption, SplitButtonGroup } from "../SplitButtonGroup";
import { useAuthContext } from "AuthContextWrapper"
import { EstateFormsContext } from "EstateFormsContextWrapper";
import AtticusFormConfirmRemoveDialog from "./AtticusFormConfirmRemoveDialog";
import Cookies from "js-cookie";
import { CONSTANTS as SHARED_CONSTANTS } from "@theatticusapp/atticus-shared-components";
import { Link } from "react-router-dom";
import { Loading } from "../Loading";
import { downloadAtticusForm } from "utils/download";
import { PDFDownloadDisclaimer } from "./PDFDownloadDisclaimer";

const dialogContentStyle = {
  height: "75vh",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  marginBottom: 0,
  position: "relative",
} 

interface Props {
  open: boolean;
  onClose: () => void;
  form?: AtticusForm;
  fileChoice?: PdfFileChoice;
  withDisclaimer?: boolean;
}

export default function AtticusFormViewDialog({
  open,
  onClose,
  form,
  fileChoice = PdfFileChoice.PROCESSED,
  withDisclaimer = false,
}: Props): JSX.Element {
  const { estateForms, addEstateForm, fetchEstateForms } = useContext(EstateFormsContext);
  const { activeEstate, user } = useAuthContext()
  const theme = useTheme();
  const isMobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const [formToRemove, setFormToRemove] = useState<AtticusForm | undefined>(undefined);
  const [fileUrl, setFileUrl] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!activeEstate?.id || !user?.id || !form) {
      return
    }
    const authToken = Cookies.get(SHARED_CONSTANTS.COOKIES.authToken) || ""
    setFileUrl(form.getFilledFormUrl(activeEstate?.id, user?.id, authToken) || undefined)
  }, [form, activeEstate, user])


  useEffect(() => {
    // Hide the "loading form" spinny after a bit.
    // It will also be covered up by the form itself so
    // this isn't strictly necessary, but we might as well not
    // have a spinny lurking behind our form waiting to leap out
    // at unsuspecting victims once an unexpected HTML issues arises
    setIsLoading(true)
    setTimeout(() => {
      setIsLoading(false)
    }, 10000)
  }, [form, fileChoice, open])

  const getFormOptions = (form:AtticusForm): SplitButtonOption[] => {
    const formOptions: SplitButtonOption[] = [];
    formOptions.push({
      label: "Download",
      key: "download",
      onClick: () => {
        if (activeEstate?.id && user?.id) {
          downloadAtticusForm(activeEstate.id, user.id, form)
        }
      }
    })
    
    if (activeEstate && form && estateForms?.find((estateForm) => estateForm.id == form.id)) {
      formOptions.push({
        label: "Remove from My Forms",
        key: "remove",
        hidden: !estateForms || !estateForms?.find((ef) => ef.id == form.id),
        onClick: async () => {
          if (form && activeEstate.id) {
            setFormToRemove(form);
          }
        },
      });
    } else if (activeEstate && estateForms) {
      formOptions.push({
        label: "Add to My Forms",
        key: "add",
        hidden: !estateForms || !form || !!estateForms?.find((ef) => ef.id == form.id),
        onClick: async () => {
          if (form && activeEstate.id && user?.id) {
            addEstateForm(activeEstate.id, user.id, form);
          }
        },
      });
    }

    return formOptions;
  };

  const onRemovedForm = () => {
    setFormToRemove(undefined);
    if (activeEstate?.id) {
      fetchEstateForms(activeEstate.id);
    }
  };

  return (<>
    <Dialog
      fullScreen={isMobileSize}
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      aria-labelledby="responsive-dialog-title"
      className="noPadding"
    >
      
      {open && form ? (
        <>
          <DialogTitle id="responsive-dialog-title" component="div">
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  flex: 1,
                  marginRight: isMobileSize ? "2.5rem" : undefined,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    wordBreak: "break-word",
                    display: "-webkit-box",
                    WebkitLineClamp: "2",
                    WebkitBoxOrient: "vertical",
                    fontWeight: "700",
                  }}
                  color="text.primary"
                >
                  {form.formGovernmentId ? `[${form.formGovernmentId}] - ` : ``}
                  {form.formTitle}
                </Typography>
                {form.geography?.fullName && (
                  <Typography variant="h6" color="text.secondary">
                    {form.geography?.fullName}
                  </Typography>
                )}
              </Box>
              {!isMobileSize && fileChoice == PdfFileChoice.PROCESSED && (
                <Box
                  sx={{
                    marginRight: "2.5rem",
                  }}
                >
                  <SplitButtonGroup
                    color="primary"
                    options={getFormOptions(form)}
                    variant="contained"
                    popperPlacement="bottom-end"
                    sx={{
                      maxWidth: "12rem",
                    }}
                  />
                </Box>
              )}
            </Box>
            {onClose ? (
              <IconButton
                aria-label="close"
                size="small"
                sx={{
                  position: "absolute",
                  right: 5,
                  top: 5,
                  color: "grey.500",
                }}
                onClick={onClose}
              >
                <CloseIcon />
              </IconButton>
            ) : null}
          </DialogTitle>
          <DialogContent sx={dialogContentStyle}>
            {isLoading && <div style={{
                display:"block", 
                position:"absolute", 
                width:"150px", 
                overflow:"visible", 
                zIndex:1, 
                left:"calc(50%-75px)", 
                top:50,
                textAlign:"center",}}>
              <Loading />
              <Typography variant="h1">Loading form...</Typography>
            </div>}
            {fileUrl && fileChoice && fileChoice != PdfFileChoice.THUMBNAIL && (
              <div style={{width:"100%", height:"100%", overflow:"hidden", zIndex:2}}>
                <object
                  style={{
                    width: "100%",
                    height: "100%",
                    zIndex: 10,
                  }}
                  data={fileUrl}
                  type="application/pdf"
                >
                    If this browser does not support PDFs. Please download the PDF
                    to view it:
                    <Link to={fileUrl}>Download PDF</Link>
                </object>
                {open && withDisclaimer && <PDFDownloadDisclaimer />}
              </div>
            )}
            {fileUrl && fileChoice && fileChoice == PdfFileChoice.THUMBNAIL && (
              <Box
                component="img"
                sx={{
                  height: "90%",
                  width: "90%",
                  objectFit: "contain",
                  filter: "drop-shadow(0px 0px 8px rgba(0,0,0,.3))",
                }}
                src={fileUrl}
              />
            )}
            {(!fileUrl || !fileChoice) && <CircularProgress />}
          </DialogContent>
          {isMobileSize && fileChoice == PdfFileChoice.PROCESSED && (
            <DialogActions>
              <SplitButtonGroup
                  color="primary"
                  options={getFormOptions(form)}
                  variant="contained"
                  popperPlacement="bottom-end"
                  sx={{
                    maxWidth: "12rem",
                  }}
                />
            </DialogActions>
          )}
        </>
      ) : (
        <DialogContent sx={dialogContentStyle}>Form Required.</DialogContent>
      )}
    </Dialog>
    {formToRemove && activeEstate?.id && user?.id ? (
      <AtticusFormConfirmRemoveDialog
        open={!!formToRemove}
        onClose={() => {
          onClose() // Closes main dialog
          onRemovedForm()
        }}
        form={formToRemove}
        estateId={activeEstate?.id}
        userId={user?.id}
      />
    ) : null}
    </>
  );
}